<template>
  <el-dialog
    :visible.sync="showDialog"
    :width="drawerWidth"
    top="35vh"
    :before-close="handleClose"
    :show-close="false"
  >
    <div class="flex flex-col gap-4 items-center justify-center">
      <div class="flex flex-col gap-4 items-center">
        <p class="text-black font-normal text-base">Session will expire in</p>
        <p class="text-5xl font-normal">
          {{ countdown }}
        </p>
      </div>
      <div class="flex gap-2.5 w-full justify-center">
        <button
          class="py-2 px-4 rounded-lg text-fg-muted font-normal border-fg-muted logout-now-button"
          @click="logoutHandler"
        >
          <span class="text-base">Logout now</span>
        </button>
        <button
          class="py-2 px-4 rounded-lg bg-primary text-white extend-session-button"
          @click="continueSessionHandler"
        >
          <span class="text-base">Extend session</span>
        </button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import breakpoints from '@/utils/breakpoints.js'
import logout from '@/mixins/logout.js'
import token from '@/mixins/token'
import refreshToken from '@/mixins/refreshToken.js'
import {
  USER_TOKEN_EXPIRY_TIME,
  MAX_INACTIVE_TIME,
  SESSION_EXPIRY_WARNING_TIME,
  TWENTY_FOUR_HOURS_IN_MILLISECONDS
} from '@/utils/constants'

export default {
  mixins: [refreshToken, logout, token], // Including necessary mixins
  data () {
    return {
      breakpoints, // Responsive design breakpoints
      showDialog: false, // Controls the visibility of the logout warning dialog
      timeout: null, // Placeholder for a timeout function
      timeLeftBeforeLogout: MAX_INACTIVE_TIME, // Initial max inactive time (13 minutes in milliseconds)
      sessionExpiryWarningTime: SESSION_EXPIRY_WARNING_TIME, // Time before logout to show warning (3 minutes in milliseconds)
      countdownId: null, // ID for the countdown interval
      isCallingRefreshToken: false, // Flag to indicate if refresh token call is in progress
      countdown: '' // Countdown timer text for the warning dialog
    }
  },
  computed: {
    // Compute the drawer width based on breakpoints for responsive design
    drawerWidth () {
      return breakpoints.lgAndUp ? '35%' : breakpoints.mdAndUp ? '70%' : '90%'
    }
  },
  methods: {
    // Remove all event listeners and clear the countdown interval
    handleRemoveEventListeners () {
      clearInterval(this.countdownId)
      document.removeEventListener('mousemove', this.handleUserActivity)
      document.removeEventListener('mousedown', this.handleUserActivity)
      document.removeEventListener('keypress', this.handleUserActivity)
      document.removeEventListener('touchstart', this.handleUserActivity)
    },

    // Handle the logout process and reset related states
    logoutHandler () {
      clearInterval(this.logoutInterval)
      this.showDialog = false
      this.logout()
    },

    // Continue the session by refreshing the token and resetting the timer
    async continueSessionHandler () {
      this.showDialog = false
      await this.refreshTokenHandler()
      this.resetTimer()
    },

    // Refresh the token and manage the refresh process
    async refreshTokenHandler () {
      this.isCallingRefreshToken = true
      await this.refreshToken(true) // Refresh the token
      await this.refreshTokenTimeout() // Handle token timeout
      this.isCallingRefreshToken = false
    },

    // Empty function to prevent modal from closing unless an action is taken
    handleClose () {},

    // Reset the inactivity timer and update the last active time
    resetTimer () {
      this.getUserRemainingTime() // Calculate remaining time
      this.updateLastActiveTime() // Update the last active time
    },

    // Handle user activity to reset the timer if the warning dialog is not shown
    handleUserActivity () {
      if (!this.showDialog) {
        this.resetTimer()
      }
    },

    // Calculate the user's remaining session time based on activity and token expiry
    getUserRemainingTime () {
      const computeUserSessionMaxInactiveTime = this.$store.state.auth
        .keepMeLoggedIn
        ? TWENTY_FOUR_HOURS_IN_MILLISECONDS // Use 24 hours if "keep me logged in" is enabled
        : MAX_INACTIVE_TIME // Otherwise, use the default max inactive time

      const currentTime = Date.now() // Current timestamp
      const browserTabLastActiveTime =
        Math.floor(
          (parseInt(localStorage.getItem('lastActiveTime'), 10) ||
            currentTime) / 1000
        ) * 1000
      const browserTabInactiveFor = currentTime - browserTabLastActiveTime
      const tokenExpiryTimeDifference =
        Number(localStorage.getItem(USER_TOKEN_EXPIRY_TIME)) -
        new Date().getTime()

      // Adjust the remaining time based on token expiry and max inactive time
      if (tokenExpiryTimeDifference < computeUserSessionMaxInactiveTime) {
        this.timeLeftBeforeLogout =
          Math.floor(tokenExpiryTimeDifference / 1000) * 1000 -
          browserTabInactiveFor
      } else if (
        tokenExpiryTimeDifference > computeUserSessionMaxInactiveTime
      ) {
        this.timeLeftBeforeLogout =
          computeUserSessionMaxInactiveTime - browserTabInactiveFor
      }
    },

    // Update the last active time in local storage
    updateLastActiveTime () {
      localStorage.setItem('lastActiveTime', Date.now().toString())
    },

    // Start the countdown timer to check for inactivity and manage session expiry
    startTimer () {
      clearInterval(this.countdownId) // Clear any existing interval
      this.countdownId = setInterval(() => {
        this.getUserRemainingTime() // Calculate remaining time
        if (this.timeLeftBeforeLogout > 0) {
          this.timeLeftBeforeLogout -= 1000 // Decrement the time left
          // Show warning dialog if remaining time is within the warning period
          if (
            this.timeLeftBeforeLogout <= this.sessionExpiryWarningTime &&
            this.timeLeftBeforeLogout > 0 &&
            !this.isCallingRefreshToken
          ) {
            this.showDialog = true
            const minutes = Math.floor(
              (this.timeLeftBeforeLogout % (1000 * 60 * 60)) / (1000 * 60)
            )
            const seconds = Math.floor(
              (this.timeLeftBeforeLogout % (1000 * 60)) / 1000
            )
            this.countdown = `${minutes}m  :  ${seconds}s` // Update countdown text
          }
        } else {
          this.logoutHandler() // Logout if time left is zero
        }
      }, 1000) // Interval of 1 second
    },

    // Initialize the timer and event listeners for user activity
    init () {
      this.startTimer()
      this.updateLastActiveTime()
      document.addEventListener('mousemove', this.handleUserActivity)
      document.addEventListener('mousedown', this.handleUserActivity)
      document.addEventListener('keypress', this.handleUserActivity)
      document.addEventListener('touchstart', this.handleUserActivity, {
        passive: true
      })
    }
  },
  mounted () {
    this.init() // Initialize the timer and event listeners when the component is mounted
  },
  destroyed () {
    this.handleRemoveEventListeners() // Remove event listeners when the component is destroyed
  }
}
</script>

<style scoped>
>>> .el-dialog {
  border-radius: 0.5rem !important;
  padding: 0px 20px 10px 10px;
}
>>> .el-dialog__body {
  padding: 0px 30px 30px;
}
>>> .el-dialog__wrapper {
  overflow: hidden;
  top: 14px;
}
>>> .el-icon-close:before {
  padding: 25px;
  opacity: 0.9;
  color: #c1c4ca;
}
>>> .el-dialog__headerbtn :hover {
  color: #c1c4ca !important ;
}

@media screen and (max-width: 768px) {
  >>> .el-dialog {
    padding: 0px 0px 10px;
  }
  >>> .el-dialog__body {
    padding: 0px 0px 20px;
  }
}
</style>
